import React from 'react';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { Hash, AtSign, MapPin, MessageSquare, Image } from 'lucide-react';
import { InstagramContent } from '../types/socialMedia.types';
import CharacterCounter from '../components/CharacterCounter';
import ScheduleSettings from '../components/ScheduleSettings';
import MediaUploader from '../components/MediaUploader';
import { PLATFORMS } from '../config/platformConfig';

interface InstagramEditorProps {
  content: InstagramContent;
  isEditing: boolean;
  onChange: (field: keyof InstagramContent, value: any) => void;
}

const quillModules = {
  toolbar: [
    ['bold', 'italic', 'underline'],
    ['link'],
    ['clean']
  ]
};

export default function InstagramEditor({ content, isEditing, onChange }: InstagramEditorProps) {
  const platform = PLATFORMS.instagram;
  const characterCount = content.text ? content.text.replace(/<[^>]*>/g, '').length : 0;
  const hashtagCount = (content.hashtags.match(/#/g) || []).length;

  return (
    <div className="space-y-4">
      {/* Post Type */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Post Type</label>
        <select
          value={content.postType}
          onChange={(e) => onChange('postType', e.target.value)}
          disabled={!isEditing}
          className="w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-pink-500 disabled:bg-gray-100"
        >
          {platform.postTypes.map(type => (
            <option key={type} value={type}>{type}</option>
          ))}
        </select>
      </div>

      {/* Caption */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Caption</label>
        {isEditing ? (
          <ReactQuill
            value={content.text || ''}
            onChange={(value) => onChange('text', value)}
            modules={quillModules}
            className="bg-white rounded"
            placeholder="Write a caption..."
          />
        ) : (
          <div
            className="px-3 py-2 bg-gray-50 rounded min-h-[100px]"
            dangerouslySetInnerHTML={{
              __html: content.text || '<span class="text-gray-400">No caption</span>'
            }}
          />
        )}
        <div className="mt-2">
          <CharacterCounter
            current={characterCount}
            limit={platform.limits.text!}
            label="Caption"
          />
        </div>
      </div>

      {/* Media Upload */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          <Image className="inline w-4 h-4 mr-1" />
          Media Files
        </label>
        <MediaUploader
          media={content.media || []}
          onChange={(media) => onChange('media', media)}
          disabled={!isEditing}
          maxFiles={content.postType === 'Carousel' ? 10 : content.postType === 'Reels' ? 1 : 1}
          acceptedTypes={content.postType === 'Reels' ? 'video/*' : 'image/*,video/*'}
          platformName="Instagram"
        />
      </div>

      {/* Hashtags */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          <Hash className="inline w-4 h-4 mr-1" />
          Hashtags
        </label>
        <textarea
          value={content.hashtags}
          onChange={(e) => onChange('hashtags', e.target.value)}
          disabled={!isEditing}
          className="w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-pink-500 disabled:bg-gray-100 h-20"
          placeholder="#hashtag1 #hashtag2 #hashtag3"
        />
        <div className="mt-1 flex items-center justify-between">
          <p className="text-xs text-gray-500">Separate with spaces</p>
          <span className={`text-xs px-2 py-0.5 rounded-full ${
            hashtagCount > 30
              ? 'bg-red-100 text-red-700'
              : 'bg-green-100 text-green-700'
          }`}>
            {hashtagCount} / 30 hashtags
          </span>
        </div>
      </div>

      {/* Mentions */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          <AtSign className="inline w-4 h-4 mr-1" />
          Mentions
        </label>
        <input
          type="text"
          value={content.mentions}
          onChange={(e) => onChange('mentions', e.target.value)}
          disabled={!isEditing}
          className="w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-pink-500 disabled:bg-gray-100"
          placeholder="@user1 @user2"
        />
        <p className="text-xs text-gray-500 mt-1">Tag accounts in your post</p>
      </div>

      {/* Location */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          <MapPin className="inline w-4 h-4 mr-1" />
          Location
        </label>
        <input
          type="text"
          value={content.location}
          onChange={(e) => onChange('location', e.target.value)}
          disabled={!isEditing}
          className="w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-pink-500 disabled:bg-gray-100"
          placeholder="New York, NY"
        />
      </div>

      {/* First Comment */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          <MessageSquare className="inline w-4 h-4 mr-1" />
          First Comment
        </label>
        <textarea
          value={content.firstComment}
          onChange={(e) => onChange('firstComment', e.target.value)}
          disabled={!isEditing}
          className="w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-pink-500 disabled:bg-gray-100 h-16"
          placeholder="Add additional hashtags or info as first comment..."
        />
        <p className="text-xs text-gray-500 mt-1">
          Great for adding extra hashtags without cluttering the caption
        </p>
      </div>

      {/* Schedule */}
      <ScheduleSettings
        scheduledDate={content.scheduledDate}
        scheduledTime={content.scheduledTime}
        onDateChange={(date) => onChange('scheduledDate', date)}
        onTimeChange={(time) => onChange('scheduledTime', time)}
        disabled={!isEditing}
        platformName="Instagram"
      />
    </div>
  );
}